/** `world.pipes` — entry pipes release queued travellers (staggered, one at a
 * time per pipe) and exit pipes swallow travellers that walk into them, which
 * either recirculate to the entry pipe that preserves their heading or leave
 * the game. Travellers sit HIDDEN while inside a pipe (still counted by the
 * phase director, never drawn or collided). */
import type { JsonObject } from '../../json.js';
import { id, int, list, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import type { EntityV1 } from '../world.js';

export type PipeConfig = {
  pipes: Array<{ id: string; x: number; y: number; side: 'left' | 'right'; role: 'entry' | 'exit' }>;
  /** Tags that recirculate through the pipes. */
  travellerTag: string;
  /** Visual pipe archetype (static) spawned at each pipe mouth. */
  archetype: string;
  /** Tag whose carriers leave the game on reaching an exit (coins). */
  vanishTag: string;
  emergeTicks: number;
  spacingTicks: number;
  /** Horizontal distance from the pipe mouth to the release point. */
  mouth: number;
};

type QueueItem = { id: string; pipe: string; at: number };

export const worldPipes = defineModule<PipeConfig>({
  id: 'world.pipes', version: 1, attach: 'global', role: 'world',
  summary: 'Entry pipes release queued travellers (staggered); exit pipes swallow them to recirculate (heading preserved) or vanish.',
  config: obj({
    pipes: list(obj({ id: id(), x: num(-2, 128), y: num(-2, 128), side: oneOf('left', 'right'), role: oneOf('entry', 'exit') }), 8, 2),
    travellerTag: id(), archetype: id(), vanishTag: id(), emergeTicks: int(0, 600), spacingTicks: int(0, 600), mouth: num(0, 4),
  }),
  requires: [], events: ['pipe-enter', 'pipe-emerge'],
  presentation: { 'pipe-enter': { cue: 'none' }, 'pipe-emerge': { cue: 'none', vfx: { effect: 'burst', palette: 2, count: 6 } } },
  proofs: ['release-staggered-per-pipe', 'exit-recirculates-preserving-heading', 'exit-vanishes-tagged', 'hidden-while-queued'],
  animStates: () => [],
  mapArchetypeRefs: (c, rename) => ({ ...c, archetype: rename(c.archetype) }),
  check(c, context) {
    const errors: string[] = [];
    if (!context.archetypes[c.archetype]?.modules.includes('body.static')) errors.push(`pipe archetype ${c.archetype} must use body.static`);
    for (const side of ['left', 'right'] as const) {
      if (!c.pipes.some(p => p.side === side && p.role === 'entry')) errors.push(`needs a ${side} entry pipe`);
      if (!c.pipes.some(p => p.side === side && p.role === 'exit')) errors.push(`needs a ${side} exit pipe`);
    }
    if (new Set(c.pipes.map(p => p.id)).size !== c.pipes.length) errors.push('pipe ids must be unique');
    return errors;
  },
  initGlobal(ctx) {
    ctx.state.queue = [];
    ctx.state.last = Object.fromEntries(ctx.config.pipes.map(p => [p.id, -100000]));
    ctx.state.alternate = ctx.randomInt(2);
    for (const p of ctx.config.pipes) ctx.spawn(ctx.config.archetype, { x: p.x, y: p.y, facing: p.side === 'left' ? 1 : -1 });
  },
  systems: {
    rules(ctx) {
      const c = ctx.config, tick = ctx.world.tick;
      const queue = ctx.state.queue as QueueItem[];
      const entry = (side: 'left' | 'right') => c.pipes.find(p => p.role === 'entry' && p.side === side)!;
      const enqueue = (e: EntityV1, side: 'left' | 'right', delay: number) => {
        e.status = 'hidden'; e.vx = 0; e.vy = 0;
        queue.push({ id: e.id, pipe: entry(side).id, at: tick + delay });
      };
      // Exits: an active grounded traveller heading into an exit mouth.
      for (const e of ctx.entities({ tag: c.travellerTag, status: 'active' })) {
        if (!e.grounded) continue;
        for (const p of c.pipes) {
          if (p.role !== 'exit' || Math.abs(e.y - p.y) > 0.3) continue;
          const into = p.side === 'left' ? e.facing === -1 && e.x <= p.x + c.mouth * 0.25 : e.facing === 1 && e.x >= p.x - c.mouth * 0.25;
          if (!into || e.tags.includes('immobile')) continue;
          ctx.emit('pipe-enter', { x: e.x, y: e.y, pipe: p.id });
          if (e.tags.includes(c.vanishTag)) ctx.despawn(e, 'pipe');
          // Heading preserved: walking left out of the left exit re-enters
          // from the right entry, still walking left.
          else enqueue(e, p.side === 'left' ? 'right' : 'left', c.emergeTicks);
          break;
        }
      }
      // Releases: earliest-due first, one per pipe per spacing window.
      queue.sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1));
      const last = ctx.state.last as JsonObject;
      for (let i = 0; i < queue.length;) {
        const item = queue[i]!;
        const e = ctx.entity(item.id);
        if (!e) { queue.splice(i, 1); continue; }
        if (item.at > tick || tick - read(last, item.pipe) < c.spacingTicks) { i++; continue; }
        const p = c.pipes.find(pp => pp.id === item.pipe)!;
        const inward = p.side === 'left' ? 1 : -1;
        e.x = p.x + inward * c.mouth; e.y = p.y; e.vx = 0; e.vy = 0; e.grounded = false; e.groundId = null;
        e.facing = inward === 1 ? 1 : -1; e.status = 'active';
        ctx.signal('pipe-emerged', e, null, { dir: inward });
        ctx.emit('pipe-emerge', { x: e.x, y: e.y, pipe: p.id });
        last[item.pipe] = tick;
        queue.splice(i, 1);
      }
    },
    // Present (the last stage): requests raised during flow this tick are
    // queued before any snapshot can show the new entity.
    present(ctx) {
      const c = ctx.config, tick = ctx.world.tick;
      const queue = ctx.state.queue as QueueItem[];
      const entry = (side: 'left' | 'right') => c.pipes.find(p => p.role === 'entry' && p.side === side)!;
      // Director requests: alternate sides unless a side is requested.
      for (const s of ctx.signals('spawn-via-pipe')) {
        const e = ctx.entity(s.target);
        if (!e) continue;
        const requested = s.data.side;
        const side = requested === 'left' || requested === 'right' ? requested : (read(ctx.state, 'alternate') % 2 === 0 ? 'left' : 'right');
        ctx.state.alternate = read(ctx.state, 'alternate') + 1;
        e.status = 'hidden';
        queue.push({ id: e.id, pipe: entry(side).id, at: tick + c.emergeTicks });
      }
    },
  },
});
